/**
 * A medication's history (QRF v1 part 2): a quiet "View history" link on a
 * Medications card row, opening the house Drawer with the medication's
 * timeline, oldest first.
 *
 *   GET /api/t/{slug}/v1/clients/{clientId}/record/medications/{rowId}/history
 *
 * Each entry reads its day, what happened, where it came from and who:
 *   "Added: Lisinopril, dose 10 mg", "Dose 10 mg to 20 mg", "Archived".
 * A field whose earlier value is not known (a medication from before the
 * history began) reads "Dose set to 7.5 mg". The read is audited on the
 * server; it runs only when the drawer opens.
 */

import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/primitives/Button';
import { Drawer } from '@/components/primitives/Drawer';
import { ErrorBanner } from '@/components/ErrorBanner';
import { apiErrorFrom } from '@/lib/api';
import { practiceDayOf } from '@/lib/practiceDate';
import { proseDate } from '@/lib/proseDate';
import { displayValue } from '@/lib/valueCase';
import { recordFieldLabel } from '@/lib/recordFormWords';

export type MedicationHistoryField = 'name' | 'dose' | 'schedule' | 'status' | 'reason' | 'started_on' | 'ended_on';

export interface MedicationHistoryEntry {
  event_id: string | null;
  at: string;
  kind: 'added' | 'changed' | 'archived' | 'restored';
  source: 'note' | 'edit' | null;
  source_note_id: string | null;
  by_name: string | null;
  changes: Array<{ field: MedicationHistoryField; from: string | null; to: string | null }>;
}

export interface MedicationHistory {
  medication_id: string;
  creation_known: boolean;
  entries: MedicationHistoryEntry[];
}

export async function fetchMedicationHistory(
  slug: string,
  clientId: string,
  rowId: string,
  signal?: AbortSignal,
): Promise<MedicationHistory> {
  const path = `/api/t/${encodeURIComponent(slug)}/v1/clients/${encodeURIComponent(clientId)}/record/medications/${encodeURIComponent(rowId)}/history`;
  const res = await fetch(path, { method: 'GET', headers: { Accept: 'application/json' }, signal });
  if (!res.ok) throw await apiErrorFrom(res, `GET ${path}`);
  return (await res.json()) as MedicationHistory;
}

/** The query key the drawer and the Client history share, so a history
 *  read once is not read again while it is fresh. */
export function medicationHistoryKey(slug: string, clientId: string, rowId: string): readonly unknown[] {
  return ['medicationHistory', slug, clientId, rowId] as const;
}

const STATUS_WORD: Record<string, string> = { active: 'active', discontinued: 'discontinued' };

function shownValue(field: MedicationHistoryField, v: string): string {
  if (field === 'started_on' || field === 'ended_on') return /^\d{4}-\d{2}-\d{2}$/.test(v) ? proseDate(v) : v;
  if (field === 'status') return STATUS_WORD[v] ?? v;
  return field === 'name' ? displayValue(v, 'medication') : displayValue(v);
}

function fieldWord(field: MedicationHistoryField): string {
  return recordFieldLabel('medications', field);
}

/** One change in words: "Dose 10 mg to 20 mg", "Dose set to 7.5 mg",
 *  "Dose cleared". */
function changeText(c: MedicationHistoryEntry['changes'][number]): string {
  const label = fieldWord(c.field);
  if (c.to === null) return `${label} cleared`;
  if (c.from === null) return `${label} set to ${shownValue(c.field, c.to)}`;
  return `${label} ${shownValue(c.field, c.from)} to ${shownValue(c.field, c.to)}`;
}

export function entryHeadline(e: MedicationHistoryEntry): string {
  if (e.kind === 'archived') return 'Archived';
  if (e.kind === 'restored') return 'Restored';
  if (e.kind === 'added') {
    const values = e.changes
      .filter((c) => c.to !== null)
      .map((c) => (c.field === 'name' ? shownValue('name', c.to!) : `${fieldWord(c.field).toLowerCase()} ${shownValue(c.field, c.to!)}`));
    return values.length > 0 ? `Added: ${values.join(', ')}` : 'Added';
  }
  return e.changes.length > 0 ? e.changes.map(changeText).join('; ') : 'Saved with no change';
}

function entryByline(e: MedicationHistoryEntry): string {
  const day = practiceDayOf(e.at);
  const parts = [day ? proseDate(day) : null, e.source === 'note' ? 'from a note' : e.source === 'edit' ? 'edited' : null];
  if (e.by_name) parts.push(`by ${e.by_name}`);
  return parts.filter(Boolean).join(' · ');
}

function HistoryBody({ clientId, rowId }: { clientId: string; rowId: string }): ReactNode {
  const { tenantSlug = '' } = useParams<{ tenantSlug: string }>();
  const q = useQuery({
    queryKey: medicationHistoryKey(tenantSlug, clientId, rowId),
    queryFn: ({ signal }) => fetchMedicationHistory(tenantSlug, clientId, rowId, signal),
  });
  if (q.isError) return <ErrorBanner error={q.error} what="this medication’s history" onRetry={() => void q.refetch()} />;
  if (!q.data) return <p className="text-sm text-ink-soft">Loading…</p>;
  return (
    <ol className="flex flex-col gap-3">
      {q.data.entries.map((e, i) => (
        <li key={e.event_id ?? `added-${i}`} data-testid="medication-history-entry" className="text-sm">
          <div className="font-medium text-sage-800 [overflow-wrap:anywhere]">{entryHeadline(e)}</div>
          <div className="text-ink-soft">{entryByline(e)}</div>
        </li>
      ))}
    </ol>
  );
}

/** The quiet "View history" link and the drawer it opens. */
export function MedicationHistoryLink({ rowId, name }: { rowId: string; name: string }): ReactNode {
  const { clientId = '' } = useParams<{ clientId: string }>();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button variant="ghost" size="sm" data-testid={`medication-history-link-${rowId}`} onClick={() => setOpen(true)}>
        View history
      </Button>
      {open && (
        <Drawer onClose={() => setOpen(false)} ariaLabel={`History of ${name}`} testId="medication-history-drawer">
          <h2 className="mb-3 text-base font-semibold text-sage-800">History: {displayValue(name, 'medication')}</h2>
          <HistoryBody clientId={clientId} rowId={rowId} />
        </Drawer>
      )}
    </>
  );
}
